import { useSuspenseQuery } from '@tanstack/react-query';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import type { Platform } from '../../../constants';
import type { CallToAction } from 'features/cabinet/tiktok/types';
import { apiClient } from 'lib/api';
import queryString from 'query-string';
import { isAxiosError } from 'axios';

export interface GetParams {
  params: {
    app: string
    adgroup_names: string[]
  };
}

export interface AdSettingEntry {
  flow_name: string,
  platform: Platform.WEB | Platform.MOBILE,
  link: string,
  call_to_action: CallToAction,
  original_text: string,
  localized_text: Record<string, string>,
}

export const getAdSettings = async (
  { params }: GetParams,
  signal?: AbortSignal,
): Promise<AdSettingEntry[]> => {
  const res = await apiClient.get<AdSettingEntry[]>(queryKeys.ad_settings, {
    params,
    signal,
    paramsSerializer: (prms) =>
      queryString.stringify(prms, { arrayFormat: 'comma' }),
  });

  if (isAxiosError(res)) {
    const error = new Error('Get Ad Settings Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

export interface UseOptions<TData> extends GetParams {
  config?: QueryConfig<typeof getAdSettings, TData>;
}

export const useAdSettings = <TData = AdSettingEntry[]>({ params, config }: UseOptions<TData>) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    refetchOnMount: true,
    ...config,
    queryFn: ({ signal }) => getAdSettings({ params }, signal),
    queryKey: [queryKeys.accounts, params.app, params.adgroup_names],
  });
};
